"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VirtualCreditCardsQuery = exports.UpdateSpendingLimitRequest = exports.VirtualCreditCardUpdateTransport = exports.VirtualCreditCardCreateTransport = exports.GetVCCAuthorizationsResponse = exports.VirtualCreditCardTransport = void 0;
const typebox_1 = require("@sinclair/typebox");
/**
 * Virtual Credit Card Transport Types
 *
 * Represents virtual credit cards issued to vendors for payment processing via SVB VCN.
 * Each vendor gets one persistent card that is locked/unlocked per payout.
 */
// Card status enum
const CardStatus = typebox_1.Type.Union([typebox_1.Type.Literal('DORMANT'), typebox_1.Type.Literal('ACTIVE'), typebox_1.Type.Literal('TERMINATED')]);
// Card type enum
const CardType = typebox_1.Type.Union([typebox_1.Type.Literal('VIRTUAL'), typebox_1.Type.Literal('PHYSICAL')]);
// Main transport schema for virtual credit cards
exports.VirtualCreditCardTransport = typebox_1.Type.Object({
    id: typebox_1.Type.String({ format: 'uuid' }),
    vendorID: typebox_1.Type.String({ format: 'uuid' }),
    svbCardId: typebox_1.Type.String({ format: 'uuid' }),
    cardNumberLast4: typebox_1.Type.Optional(typebox_1.Type.String()),
    expirationDate: typebox_1.Type.Optional(typebox_1.Type.String()), // YYYY-MM format
    status: CardStatus,
    spendingLimit: typebox_1.Type.Integer({ minimum: 0 }),
    spendingLimitDollars: typebox_1.Type.Number({ minimum: 0 }),
    currentSpend: typebox_1.Type.Integer({ minimum: 0 }),
    currentSpendDollars: typebox_1.Type.Number({ minimum: 0 }),
    // Live spendable balance in cents, read from SVB rather than our own record.
    // Only populated by endpoints that fetch the card from SVB, and optional
    // because SVB omits it on some responses. Deliberately not derived from
    // `spendingLimit - currentSpend`: `currentSpend` only advances when a
    // clearing webhook lands, so it lags the real balance by days during exactly
    // the settlement gap reconciliation cares about. See utils/assert-no-funds-drawn.ts.
    availableBalance: typebox_1.Type.Optional(typebox_1.Type.Integer()),
    linkedInvoiceIDs: typebox_1.Type.Optional(typebox_1.Type.Array(typebox_1.Type.String({ format: 'uuid' }))),
    cardType: typebox_1.Type.Optional(CardType),
    activatedAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    expiresAt: typebox_1.Type.Optional(typebox_1.Type.String()), // ISO 8601 timestamp
    notes: typebox_1.Type.Optional(typebox_1.Type.String()),
    // Computed flags
    isActive: typebox_1.Type.Boolean(),
    isDormant: typebox_1.Type.Boolean(),
    isTerminated: typebox_1.Type.Boolean(),
    // Entity metadata
    metadata: typebox_1.Type.Optional(typebox_1.Type.Any()),
});
// Response for GET /invoice/vendor-onboarding/vcc/:id/authorizations
exports.GetVCCAuthorizationsResponse = typebox_1.Type.Object({
    card: exports.VirtualCreditCardTransport,
    authorizations: typebox_1.Type.Array(typebox_1.Type.Object({
        approvalCode: typebox_1.Type.Optional(typebox_1.Type.String()),
        issuerResponse: typebox_1.Type.String(),
        mcc: typebox_1.Type.Optional(typebox_1.Type.String()),
        mccDescription: typebox_1.Type.Optional(typebox_1.Type.String()),
        merchantName: typebox_1.Type.Optional(typebox_1.Type.String()),
        merchantId: typebox_1.Type.Optional(typebox_1.Type.String()),
        billing: typebox_1.Type.Object({ value: typebox_1.Type.Integer(), currencyCode: typebox_1.Type.String() }),
        merchant: typebox_1.Type.Object({ value: typebox_1.Type.Integer(), currencyCode: typebox_1.Type.String() }),
        transactionDateTime: typebox_1.Type.Optional(typebox_1.Type.String()),
        vcnUuid: typebox_1.Type.String(),
        banknetReferenceNumber: typebox_1.Type.Optional(typebox_1.Type.String()),
        clearingReferenceNumber: typebox_1.Type.Optional(typebox_1.Type.String()),
        cardHolderTransactionTypeCode: typebox_1.Type.Optional(typebox_1.Type.String()),
        vcnResponse: typebox_1.Type.Optional(typebox_1.Type.String()),
        settlementDate: typebox_1.Type.Optional(typebox_1.Type.String()),
        purchaseRequestId: typebox_1.Type.Optional(typebox_1.Type.String()),
        financialNetworkCode: typebox_1.Type.Optional(typebox_1.Type.String()),
    })),
});
// Create request for issuing a new virtual credit card
exports.VirtualCreditCardCreateTransport = typebox_1.Type.Object({
    vendorID: typebox_1.Type.String({ format: 'uuid' }),
    spendingLimit: typebox_1.Type.Integer({ minimum: 0 }),
    linkedInvoiceIDs: typebox_1.Type.Optional(typebox_1.Type.Array(typebox_1.Type.String({ format: 'uuid' }))),
    notes: typebox_1.Type.Optional(typebox_1.Type.String()),
});
// Update request for modifying card settings
exports.VirtualCreditCardUpdateTransport = typebox_1.Type.Object({
    spendingLimit: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    linkedInvoiceIDs: typebox_1.Type.Optional(typebox_1.Type.Array(typebox_1.Type.String({ format: 'uuid' }))),
    notes: typebox_1.Type.Optional(typebox_1.Type.String()),
});
// Request to update spending limit
exports.UpdateSpendingLimitRequest = typebox_1.Type.Object({
    newLimit: typebox_1.Type.Integer({ minimum: 0 }),
    reason: typebox_1.Type.Optional(typebox_1.Type.String()),
});
// Query parameters for listing virtual credit cards
exports.VirtualCreditCardsQuery = typebox_1.Type.Object({
    limit: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 1, maximum: 100 })),
    offset: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    vendorID: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    status: typebox_1.Type.Optional(CardStatus),
    minSpendingLimit: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    maxSpendingLimit: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
    hasAvailableBalance: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
    linkedToInvoice: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
    sortBy: typebox_1.Type.Optional(typebox_1.Type.Union([
        typebox_1.Type.Literal('spendingLimit'),
        typebox_1.Type.Literal('currentSpend'),
        typebox_1.Type.Literal('createdAt'),
        typebox_1.Type.Literal('expiresAt'),
    ])),
    sortOrder: typebox_1.Type.Optional(typebox_1.Type.Union([typebox_1.Type.Literal('asc'), typebox_1.Type.Literal('desc')])),
});
//# sourceMappingURL=virtual-credit-card-transport.js.map